import React, { RefObject, useEffect, useState } from "react";
import style from "./style.module.scss";
import { Flag } from "@jmc/solid-design-system/src/components/atoms/Flag/Flag";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { TextField } from "@jmc/solid-design-system/src/components/atoms/TextField/TextField";
import { InputLabel } from "@jmc/solid-design-system/src/components/atoms/InputLabel/InputLabel";
import { Select } from "@jmc/solid-design-system/src/components/atoms/Select/Select";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Region, getCountries, Country, getCountry } from "../../../utils/countries";
import { mdiCheck } from "@mdi/js";
import classnames from "classnames";

import { parsePhoneNumberFromString } from "libphonenumber-js";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";

export interface PhoneNumber extends Country {
    nationalNumber: string;
    valid: boolean;
}

export function parsePhoneNumber(nr: string): PhoneNumber | undefined {
    const parsed = parsePhoneNumberFromString(nr);
    if (parsed) {
        const country = getCountry(parsed?.country);
        return {
            ...country,
            nationalNumber: parsed?.nationalNumber.toString(),
            valid: parsed?.isValid(),
        };
    }
}

interface PropTypes {
    name?: string;
    value?: string;
    label?: string;
    countryCode?: string;
    region?: Region;
    disabled?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    third?: boolean;
    gap?: "spacing-03";
    handlePhoneChange?: (arg: string) => void;
    required?: boolean;
}

interface PhoneNumberInputState {
    dialCode?: string;
    number?: string;
    countryCode?: string;
}

const PhoneNumberInput = (props: PropTypes) => {
    const { jnjFullBranded } = useJnjBranding();

    const {
        name,
        value = "",
        label,
        countryCode = "be",
        region,
        disabled = false,
        third = false,
        handlePhoneChange = () => {},
        inputRef,
        gap, // TODO: Rebranding: We should not need this parameter
        required = false,
    } = props;

    const countryCodes = getCountries(region);
    const textFieldRef: React.RefObject<HTMLInputElement> = React.createRef();

    const [state, setState] = useState<PhoneNumberInputState>({
        dialCode: "",
        number: "",
        countryCode: "",
    });

    useEffect(() => {
        const phoneNumber = parsePhoneNumber(value);
        if (phoneNumber) {
            setState(() => ({
                dialCode: phoneNumber?.dialCode,
                number: phoneNumber?.nationalNumber,
                countryCode: phoneNumber?.countryCode,
            }));
        } else {
            const defaultCountry = getCountry(countryCode?.toUpperCase());
            setState({
                dialCode: defaultCountry?.dialCode,
                countryCode: defaultCountry?.countryCode,
                number: null,
            });
        }
    }, [countryCode]);

    const onChange = (selected: unknown) => {
        textFieldRef.current.focus();
        setState({
            dialCode: selected.value.dialCode,
            countryCode: selected.value.countryCode,
        });
    };

    const fullNumber = `+${state.dialCode}${state.number || ""}`;
    const parsedNumber = parsePhoneNumber(fullNumber);

    return (
        <div className={style.phonenumber}>
            <input name={name} type="hidden" ref={inputRef} value={fullNumber} disabled={disabled} />
            {label && !jnjFullBranded && (
                <div data-test-id="PhoneNumberInput.Label">
                    <Typography variant="label">{label}</Typography>
                </div>
            )}
            {label && jnjFullBranded && (
                <InputLabel label={label} required={required} data-test-id="PhoneNumberInput.Label" />
            )}
            <div className={classnames(style["phonenumber__field"], gap ? style[`phonenumber__gap-${gap}`] : null)}>
                <div
                    className={classnames(third ? style["phonenumber__dropdownthird"] : style["phonenumber__dropdown"])}
                >
                    <Select
                        onChange={(selected) => onChange(selected)}
                        isSearchable={false}
                        disabled={disabled}
                        data-test-id="PhoneNumberInput.Select"
                        menu={{ width: "var(--select-menu-width, 100%)" }}
                        control={{ height: jnjFullBranded ? undefined : "39px" }}
                    >
                        {countryCodes.map((country: Country) => (
                            <Select.Option
                                key={country.countryCode}
                                value={country}
                                selected={state.countryCode === country.countryCode}
                                data-test-id={`PhoneNumberInput.Option.${country.countryCode}`}
                            >
                                <div className={style["phonenumber__option"]}>
                                    <div
                                        className={style["phonenumber__flag"]}
                                        data-test-id={`PhoneNumberInput.Option.${country.countryCode}.flag`}
                                    >
                                        <Flag code={country.countryCode} />
                                    </div>
                                    <div
                                        className={style["phonenumber__country"]}
                                        data-test-id={`PhoneNumberInput.Option.${country.countryCode}.country`}
                                    >
                                        <span
                                            className={
                                                style[`${third ? "phonenumber__longthird" : "phonenumber__long"}`]
                                            }
                                        >
                                            {country.countryName}
                                        </span>
                                        <span
                                            className={
                                                style[`${third ? "phonenumber__shortthird" : "phonenumber__short"}`]
                                            }
                                        >
                                            {country.countryCode}
                                        </span>
                                    </div>
                                </div>
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className={classnames(style["phonenumber__number"], gap ? style["phonenumber__gap"] : null)}>
                    <TextField
                        name="phonenumber"
                        ref={textFieldRef}
                        value={state.number || ""}
                        type="number"
                        disabled={disabled}
                        onChange={(e) => {
                            setState({ ...state, number: e.target.value });
                            handlePhoneChange(`+${state.dialCode}${e.target.value || ""}`);
                        }}
                        data-test-id={`PhoneNumberInput.TextField`}
                        startAdornment={
                            <Typography
                                size="s"
                                color="inherit"
                                wordBreak="normal"
                                data-test-id={`PhoneNumberInput.TextField.prefix`}
                            >
                                +{state.dialCode}
                            </Typography>
                        }
                        endAdornment={
                            parsedNumber &&
                            parsedNumber.valid && (
                                <span className={style["phonenumber__validation"]}>
                                    <Icon
                                        icon={mdiCheck}
                                        color="success"
                                        data-test-id={`PhoneNumberInput.TextField.validation`}
                                    />
                                </span>
                            )
                        }
                    />
                </div>
            </div>
        </div>
    );
    // }
};

export default React.forwardRef((props: PropTypes, ref: RefObject<HTMLInputElement>) => (
    <PhoneNumberInput inputRef={ref} {...props} />
));
